/**
 * @file NotificationSettings.tsx
 * @brief Profile section: one switch per notification category, and quiet hours.
 *
 * @details
 * Same settings as the phone's Notifications screen (FRONTEND
 * NotificationSettingsScreen.tsx); they live on the account and drive the
 * phone's pushes and the desktop app's notifications. Security cannot be
 * turned off. The web itself gets no notifications yet (web push is a
 * post-launch item), so the copy says where they appear.
 *
 * Layout: the GridBand section pattern of ConnectedAiApps, under it on the
 * Profile page.
 *
 * @author DES
 * @date 09-10-2026
 */

import { Switch } from "@/components/ui/Switch";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { toast } from "@/shared/hooks/useToast";
import { browserTimeZone, usePushPreferences, useSavePushPreferences, type PushCategoryId } from "./api";

// What each category sends (wording from the Phase H table).
const DESCRIPTIONS: Record<PushCategoryId, string> = {
  meetings: "When your notes or Meeting Intelligence are ready, or a meeting could not be processed",
  reminders: "15 minutes before a scheduled meeting or interpretation",
  collaboration: "When someone shares a meeting or note with you, or adds notes to your meeting",
  tasks: "When a to-do from a meeting is assigned to you",
  billing: "A failed payment, and when your Intelligence minutes run low",
  security: "A sign-in to your account from a new device. Always on.",
  retention: "Before recordings are deleted, so you can export them",
};

export function NotificationSettings() {
  const prefs = usePushPreferences();
  const save = useSavePushPreferences();
  const data = prefs.data;

  const update = async (patch: { disabledCategories?: PushCategoryId[]; quietHours?: boolean }) => {
    try {
      await save.mutateAsync({ ...patch, timeZone: browserTimeZone() });
    } catch {
      toast({ title: "Could not save", description: "Please try again.", variant: "destructive" });
    }
  };

  const toggle = (id: PushCategoryId, on: boolean) => {
    if (!data) return;
    const set = new Set(data.disabledCategories);
    if (on) set.delete(id);
    else set.add(id);
    void update({ disabledCategories: [...set] });
  };

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">
        Notifications
        <span className="ml-1 font-normal text-muted-foreground">
          · On your phone and in the Verblizr desktop app. A notification shows a title, never what was said.
        </span>
      </GridBand>
      {!data ? (
        <div className="px-4 py-3.5 text-sm text-muted-foreground md:px-5">
          {prefs.isError ? "Could not load your notification settings." : "Loading…"}
        </div>
      ) : (
        <ul>
          {data.categories.map((c) => {
            const on = c.locked || !data.disabledCategories.includes(c.id);
            return (
              <li key={c.id} className="flex items-center gap-3 border-b border-border/60 px-4 py-3.5 md:px-5">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{c.label}</span>
                  <span className="block text-xs text-muted-foreground">{DESCRIPTIONS[c.id]}</span>
                </span>
                <Switch
                  checked={on}
                  disabled={c.locked || save.isPending}
                  onCheckedChange={(v: boolean) => toggle(c.id, v)}
                  aria-label={`${c.label} notifications`}
                />
              </li>
            );
          })}
          <li className="flex items-center gap-3 px-4 py-3.5 md:px-5">
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">Quiet hours</span>
              <span className="block text-xs text-muted-foreground">
                22:00 to 07:00. Notifications wait until 07:00, except Reminders and Security.
              </span>
            </span>
            <Switch
              checked={data.quietHours}
              disabled={save.isPending}
              onCheckedChange={(v: boolean) => void update({ quietHours: v })}
              aria-label="Quiet hours"
            />
          </li>
        </ul>
      )}
    </div>
  );
}
